import {
  Box,
  Button,
  Card,
  Flex,
  IconButton,
  Progress,
  Text,
} from "@chakra-ui/react";
import { Step } from "nextstepjs";
import { LuArrowLeft, LuArrowRight } from "react-icons/lu";
import { useOnboardingNewbie } from "@/_hooks/useOnboardingNewbie";
import { IoCheckmarkDoneCircle } from "react-icons/io5";
import { MdAirlineStops } from "react-icons/md";

interface CustomCardProps {
  step: Step;
  currentStep: number;
  totalSteps: number;
  nextStep: () => void;
  prevStep: () => void;
  skipTour?: () => void;
  arrow?: React.ReactNode;
}

export const CustomStepCard = (props: CustomCardProps) => {
  const { step, currentStep, totalSteps, nextStep, prevStep, skipTour, arrow } =
    props;
  const { completeOnboarding } = useOnboardingNewbie();

  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;
  const onSkipOrFinish = () => {
    completeOnboarding();
    skipTour?.();
  };

  return (
    <Card.Root
      flexDirection="row"
      size="md"
      shadow="cardsm"
      rounded="3xl"
      position="relative"
      zIndex="modal"
      m={1}
      maxW="30rem"
      minW="26rem"
    >
      <Box>
        <Card.Body p={4}>
          <Flex mb={4} align="center" gap={2} color="primary.500">
            {step.icon && <Box flexShrink={0}>{step.icon}</Box>}
            <Text textStyle="md" fontWeight="bold">
              {step.title}
            </Text>
          </Flex>
          {typeof step.content === "string" ? (
            <Text mb={4} textStyle="sm" color="neutral.700" lineHeight="tall">
              {step.content}
            </Text>
          ) : (
            step.content
          )}
          <Progress.Root
            size="xs"
            colorPalette="primary"
            max={totalSteps}
            value={currentStep + 1}
            rounded="full"
          >
            <Progress.Track rounded="full">
              <Progress.Range />
            </Progress.Track>
          </Progress.Root>
        </Card.Body>
        <Card.Footer>
          <Flex
            gap={2}
            alignItems="center"
            justifyContent="space-between"
            w="full"
          >
            <Flex gap={2}>
              {step.showControls && (
                <>
                  <IconButton
                    colorPalette="gray"
                    size="sm"
                    onClick={prevStep}
                    visual="ghost"
                    disabled={isFirstStep}
                  >
                    <LuArrowLeft />
                  </IconButton>
                  <IconButton
                    colorPalette="gray"
                    size="sm"
                    onClick={nextStep}
                    visual="ghost"
                    disabled={isLastStep}
                  >
                    <LuArrowRight />
                  </IconButton>
                </>
              )}
            </Flex>
            {step.showSkip && (
              <Button
                size="xs"
                colorPalette={isLastStep ? "primary" : "neutral"}
                onClick={onSkipOrFinish}
                visual="outline"
                h={6}
                px={1}
                pr={2}
              >
                {isLastStep ? <IoCheckmarkDoneCircle /> : <MdAirlineStops />}
                {isLastStep ? "Finish" : "Skip"}
              </Button>
            )}
          </Flex>
        </Card.Footer>
      </Box>

      {arrow}
    </Card.Root>
  );
};
